import React, { useEffect, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import ReactPlayer from 'react-player';

const PlayerStatus = {
  PENDING: 'PENDING',
  ALLOWED: 'ALLOWED',
  BLOCKED: 'BLOCKED',
};

const AutoplayVideoPlayer = ({
  url,
  fallbackImage,
  className = '',
  onStatusChange,
  containerStyle = {},
  playerStyle = {},
  onReady,
  onError

}) => {
  // Get lowPowerModeStatus from Redux instead of props
  const lowPowerModeStatus = useSelector(state => state.device?.lowPowerModeStatus);
  const playerRef = useRef(null);

  // Use low power mode status from parent service
  const shouldBlockVideo = useMemo(() => {
    // If lowPowerModeStatus is null, we're still detecting, so allow video by default
    if (!lowPowerModeStatus) {
      //console.log('Low power mode detection not complete, allowing video by default');
      return false;
    }
    
    // TEMPORARY: For iPhone, be more aggressive - block video if it's an iPhone and either:
    // 1. Low power mode is detected, OR
    // 2. Detection couldn't determine low power mode reliably
    if (lowPowerModeStatus.deviceType === 'iPhone') {
      // If we explicitly detected normal mode (isLowPowerMode === false), allow video
      if (lowPowerModeStatus.isLowPowerMode === false) {
      //  console.log('iPhone in normal mode - allowing video:', lowPowerModeStatus);
        return false;
      }
      // Otherwise (low power mode or uncertain), block video for safety
      //console.log('iPhone detected - blocking video for battery safety:', lowPowerModeStatus);
      return true;
    }
    
    // For non-iPhone iOS devices, use the original logic
    if (lowPowerModeStatus.isIOSDevice && lowPowerModeStatus.isLowPowerMode) {
      //console.log('Video blocked due to iOS device in low power mode:', lowPowerModeStatus);
      return true;
    }
    
    // All other cases: allow video (non-iOS devices, iOS not in low power mode, etc.)
    //console.log('Video allowed - not iOS low power mode combination:', lowPowerModeStatus);
    return false;
  }, [lowPowerModeStatus]);

  // Handle video blocking based on low power mode status from parent service
  useEffect(() => {
    if (shouldBlockVideo) {
      //console.log('Showing fallback image for iOS device in low power mode');
      onStatusChange?.(PlayerStatus.BLOCKED);
      return;
    }

    // Not iOS low power mode combination, proceed with normal video playback
   // console.log('Not iOS low power mode combination, proceeding with video playback');
  }, [shouldBlockVideo, onStatusChange]);

  // Test video autoplay capability
  useEffect(() => {
    // Always test autoplay capability, even for iOS low power mode
    // The fallback display is handled by shouldShowFallback logic
    
    // Create a temporary video element for testing autoplay
    const testVideo = document.createElement('video');
    testVideo.muted = true;
    testVideo.playsInline = true;
    testVideo.src = url;

    const playPromise = testVideo.play();

    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          //console.log('Autoplay test successful');
          onStatusChange?.(PlayerStatus.ALLOWED);
        })
        .catch((error) => {
         // console.log('Browser autoplay blocked:', error.name);
          onStatusChange?.(PlayerStatus.BLOCKED);
        });
    }

    return () => {
      testVideo.pause();
      testVideo.removeAttribute('src');
      testVideo.load();
    };
  }, [url, onStatusChange]);

  // Always render both elements with smooth transitions
  // Conservative approach: Show fallback for iPhone devices in low power mode
  const shouldShowFallback = useMemo(() => {
    // if (!lowPowerModeStatus) {
    //   // During detection, be conservative for iPhone
    //   const isiPhone = /iPhone/i.test(navigator.userAgent);
    //   return isiPhone; // Show fallback by default for iPhone during detection
    // }
    
    if (!lowPowerModeStatus) {
      return false;
    }
    
    // For iPhone: show fallback if low power mode detected OR if we couldn't determine reliably
    if (lowPowerModeStatus.deviceType === 'iPhone') {
      // Only allow video if we're explicitly sure it's NOT in low power mode
      return lowPowerModeStatus.isLowPowerMode !== false;
    }
    
    // For other iOS devices: show fallback only when explicitly in low power mode
    return lowPowerModeStatus.isIOSDevice && lowPowerModeStatus.isLowPowerMode;
  }, [lowPowerModeStatus]);

  // // Debug logging for iPhone low power mode
  // useEffect(() => {
  //   console.log('=== AutoplayVideoPlayer Debug ===');
  //   console.log('lowPowerModeStatus:', lowPowerModeStatus);
  //   console.log('shouldShowFallback:', shouldShowFallback);
  //   console.log('shouldBlockVideo:', shouldBlockVideo);
  //   if (lowPowerModeStatus) {
  //     console.log('isIOSDevice:', lowPowerModeStatus.isIOSDevice);
  //     console.log('isLowPowerMode:', lowPowerModeStatus.isLowPowerMode);
  //     console.log('deviceType:', lowPowerModeStatus.deviceType);
  //   }
  //   console.log('================================');
  // }, [lowPowerModeStatus, shouldShowFallback, shouldBlockVideo]);

  return (
    <div
      style={{
        position: 'fixed',
        width: '100vw',
        height: '100vh',
        zIndex: -1,
        overflow: 'hidden',
        ...containerStyle,
      }}
      className={className}
    >
      {/* Debug Panel for iPhone Testing - Only in development */}
      {/* {process.env.NODE_ENV === 'development' && (
        <div style={{
          position: 'absolute',
          top: '40px',
          right: '20px',
          background: 'rgba(0,0,0,0.9)',
          color: 'white',
          padding: '10px',
          fontSize: '11px',
          zIndex: 1000,
          borderRadius: '5px',
          maxWidth: '250px',
          fontFamily: 'monospace'
        }}>
          <div>Device: {lowPowerModeStatus?.deviceType || 'Detecting...'}</div>
          <div>iOS: {lowPowerModeStatus?.isIOSDevice ? 'Yes' : 'No'}</div>
          <div>Low Power: {lowPowerModeStatus?.isLowPowerMode ? 'Yes' : 'No'}</div>
          <div>Show Fallback: {shouldShowFallback ? 'Yes' : 'No'}</div>
          <div>iPhone UA: {navigator.userAgent.includes('iPhone') ? 'Yes' : 'No'}</div>
          <div>Reduced Motion: {window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'Yes' : 'No'}</div>
        </div>
      )} */}
      {/* Fallback image - always rendered */}
      <div
        style={{
          backgroundImage: `url(${fallbackImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center center',
          backgroundRepeat: 'no-repeat',
          position: 'absolute',
          width: '100%',
          height: '100%',
          opacity: shouldShowFallback ? 1 : 0,
          transition: 'opacity 0.8s ease-in-out',
          zIndex: -2,
        }}
        role="img"
        aria-label="Background image when video cannot play"
      />
      
      {/* Video player - always rendered */}
      <ReactPlayer
        ref={playerRef}
        url={url}
        className="video-player"
        playing={true}
        loop={true}
        muted={true}
        playsinline={true}
        controls={false}
        width="100%"
        height="100%"
        style={{
          // position: 'absolute',
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          opacity: shouldShowFallback ? 0 : 1,
          transition: 'opacity 0.8s ease-in-out',
          zIndex: -1,
          ...playerStyle,
        }}
        config={{
          file: {
            attributes: {
              playsInline: true,
              controls: false,
              autoPlay: true,
              muted: true,
              'webkit-playsinline': 'true',
              'x5-video-player-type': 'h5',
              'x5-video-player-fullscreen': 'false',
              poster: fallbackImage,
              preload: 'auto',
            },
            forceVideo: true,
          },
        }}
        onReady={() => {
         // console.log('Video ready');
          onReady?.(); // Call the prop callback if provided
        }}
        onStart={() => {
          //console.log('Video started playing');
        }}
        onPlay={() => {
         // console.log('Video playing');
        }}
        onError={(e) => {
         // console.log('Video playback error:', e);
          onStatusChange?.(PlayerStatus.BLOCKED);
          onError?.(e); // Call the prop callback if provided
        }}
      />
    </div>
  );
};

export default AutoplayVideoPlayer;